import { useCallback, useEffect, useState, CSSProperties } from "react";
import {
    EnterArtistContainer,
    EnterArtistLabel,
    PlatformLogoContainer,
} from "pages/AddArtist/style"
import { checkIfSpotifyArtistExistsForUser_api } from "api/brandApi";
import { SpotifyArtistInBrandConnections, SpotifyArtistSearchResponse } from "modules/Types";
import { debounce } from 'lodash';
import { ReactComponent as SpotifyIcon } from "assets/images/streaming/icons/spotify-i.svg";
import NotFoundArtistLabel from "pages/AddArtist/Components/NotFoundArtistLabel";
import InputSymphony from "../../elements/InputSymphony";
import useScreen from "Hooks/useScreen";
import { searchSpotifyArtists_api } from "api/spotifyApi";
import AlreadyExistingArtistModal from "components/shareable/AlreadyExistingArtistModal";
import { clsx, styx } from "modules/Utils";
import ArtistList from "components/shareable/ArtistList";
import { CurrentBrand } from "modules/Types";
import useCurrentBrand from "Hooks/useCurrentBrand";

interface SpotifyArtistSearchProps {
    className: string
    style?: CSSProperties
    title: string
    onSelectArtist: (artist: SpotifyArtistSearchResponse | null, existingBrand?: CurrentBrand | null) => void
    onClickCreateNewProfile: (profileName: string) => void
}

const SpotifyArtistSearch = ({
    className,
    style,
    title,
    onSelectArtist,
    onClickCreateNewProfile
}: SpotifyArtistSearchProps) => {
    const { mobileView } = useScreen();
    const { currentBrand, setCurrentBrand } = useCurrentBrand()
    /** Search string entered by user */
    const [searchText, setSearchStr] = useState<string>("");
    /** Loading state for search operations */
    const [loadingSearch, setLoadingSearch] = useState<boolean>(false);
    /** Results from artist search */
    const [searchResults, setSearchResults] = useState<Array<SpotifyArtistSearchResponse>>([]);
    /** Source of search results */
    const [searchSource, setSearchSource] = useState<string>("");
    const [showAlreadyExistsModal, setShowAlreadyExistsModal] = useState<boolean>(false);
    const [isLoadingSelectedArtist, setIsLoadingSelectedArtist] = useState<boolean>(false);
    const [selectedSpotifyArtist, setSelectedSpotifyArtist] = useState<SpotifyArtistSearchResponse | SpotifyArtistInBrandConnections | null>(null);

    /** Debounced function to search artists via API */
    const debouncedSearchArtists = useCallback(
        debounce(async (searchValue: string) => {
            setLoadingSearch(true);
            try {
                const artists = await searchSpotifyArtists_api(searchValue, 30);
                setSearchResults(artists);
                setSearchSource("spotify");
            } catch (error) {
                console.error("Error fetching artists:", error);
                setSearchResults([]);
            } finally {
                setLoadingSearch(false);
            }
        }, 1200),
        [] // empty dependency array => stable reference
    );

    /** Cleanup debounce on unmount */
    useEffect(() => {
        return () => {
            debouncedSearchArtists.cancel();
        };
    }, []);

    /** Handles input change and triggers debounced search */
    const handleChangeInputText = (valueText: string): void => {
        const value = valueText;
        setSearchStr(value);
        if (value.length) {
            setLoadingSearch(true);
            debouncedSearchArtists(value);
        } else {
            setSearchResults([]);
            setSearchSource('');
            setLoadingSearch(false);
            onSelectArtist(null)
        }
    };

    const selectArtistFromList = async (artist: SpotifyArtistInBrandConnections | SpotifyArtistSearchResponse) => {
        // 1. Check if the artist already exists for user
        // 2. if so, show an "Already exists" modal
        // 3. if not, continue thru creation flow 
        setSelectedSpotifyArtist(artist)
        try {
            setIsLoadingSelectedArtist(true)

            // 1. check if the artist exists for user
            const res = await checkIfSpotifyArtistExistsForUser_api(artist?.id || null)
            const {
                data: existingBrand,
                error: isAlreadyExistingBrandError
            }: {
                data: CurrentBrand | null
                error: unknown | null
            } = res

            if (existingBrand || isAlreadyExistingBrandError) {
                setCurrentBrand(existingBrand!)
                setShowAlreadyExistsModal(true)
            } else {
                // 3. if not, the artist exists and the user can continue thru creation flow 
                setShowAlreadyExistsModal(false)
                onSelectArtist(artist as SpotifyArtistSearchResponse, existingBrand)
            }
        } catch (e) {
            console.log("error checking if artist exists", e)
        } finally {
            setIsLoadingSelectedArtist(false)
        }
    }

    return (
        <>
            <EnterArtistContainer
                className={clsx("spotifyArtistSearchComponent", className)}
                style={styx(style)}
            >
                <EnterArtistLabel className="enterArtistLabel">
                    {title}
                </EnterArtistLabel>
                <div className="artistInputContainer" style={sty.artistInputContainer}>
                    <PlatformLogoContainer backgroundColor='#191919' padding={mobileView ? '8px' : '13px'}>
                        <SpotifyIcon height={mobileView ? '32px' : '38px'} width={mobileView ? '32px' : '38px'} />
                    </PlatformLogoContainer>
                    <InputSymphony
                        className="artistInputSpotifySearch"
                        value={searchText}
                        onChange={(valueText) => handleChangeInputText(valueText)}
                        isLoading={loadingSearch}
                        placeholder="Bad Bunny, SZA..."
                    />
                </div>
                {searchResults?.length > 0 &&
                    <ArtistList
                        className="artistListFindProfile"
                        artists={searchResults}
                        selectArtist={selectArtistFromList}
                        selectedArtist={selectedSpotifyArtist}
                        isLoadingSelectedArtist={isLoadingSelectedArtist}
                    />
                }
                {!loadingSearch && searchText &&
                    <NotFoundArtistLabel
                        className="notFoundArtistLabelFindProfile"
                        searchStr={searchText}
                        onClickCreateNewProfile={() => onClickCreateNewProfile(searchText)}
                    />
                }
            </EnterArtistContainer>

            {/* Modals */}
            <AlreadyExistingArtistModal
                className="alreadyExistingArtistModalFindProfile"
                isOpen={showAlreadyExistsModal}
                onClose={() => setShowAlreadyExistsModal(false)}
                brandOverride={currentBrand}
                artist={selectedSpotifyArtist as SpotifyArtistSearchResponse}
            />
        </>
    )
}

const sty: Record<string, CSSProperties> = {
    artistInputContainer: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-start",
        alignItems: "center",
        width: "100%",
        gap: "16px"
    },
}

export default SpotifyArtistSearch